import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getConvBusy, type ConvBusyAgent } from '../servers/convBusy'
import { onChatv1Frame, onChatv1Open } from '../chatv1/ws'
import { onChatBusy } from '../servers/commandStream'

/**
 * What THIS conversation's agents are doing — for every member, not only the owner.
 *
 * The owner's own halo (useIdleStatus) asks their OWN machines and cannot see a
 * friend's agents; this asks the BFF, which checks conversation membership and
 * relays the same /agent/idle_status question to each agent's hosting machine over
 * the link it already holds. Same source of truth, so the two views cannot tell
 * different stories — this one is just fetched on the room's signals.
 *
 * NOT A POLL (the no-heartbeat rule; servers/noHeartbeat.test.ts is the fence).
 * It re-asks on real events only:
 *   - the chat socket (re)opening — reconcile-on-open, like every chat view;
 *   - a message frame in THIS conversation — someone spoke, so an agent is about
 *     to start working (ask again shortly after, once, for the turn's onset) or
 *     just finished (the immediate ask catches the halo going out);
 *   - the page becoming visible — the PWA-resume case, same as useIdleStatus.
 */
const TURN_ONSET_MS = 2_500

/**
 * How long an 'active' answer may stand WITHOUT reconfirmation.
 *
 * A friend gets NO event when someone else's agent finishes silently: the owner's
 * machine streams nothing to them (the fan-out diet), and a turn that ends without
 * posting into the room fires no message frame. So an unrefreshed 'active' stood
 * for ever — the stuck halo the owner reported (2026-08-27: Agent 已經不忙碌了…
 * 朋友那裡還是顯示忙碌，Owner 的已經消失). While a halo is LIT and the page
 * visible, the claim is re-confirmed once per lease; the moment the answer has no
 * active agent nothing further is scheduled. The StartAgentSheet class: a net
 * where no event can exist — not a heartbeat, and it stops at idle.
 */
const ACTIVE_LEASE_MS = 20_000

export function useConvBusy(convId: string | null | undefined): Map<string, ConvBusyAgent> {
  const q = useQuery({
    queryKey: ['conv-busy', convId ?? ''],
    queryFn: () => getConvBusy(convId!),
    enabled: !!convId,
    staleTime: 0,
  })

  useEffect(() => {
    if (!convId) return
    let onset: ReturnType<typeof setTimeout> | null = null
    const again = () => void q.refetch()
    const offOpen = onChatv1Open(again)
    const offFrame = onChatv1Frame((f) => {
      if (f.type !== 'message' || f.conv_id !== convId) return
      // Now, for the turn that just ENDED (an agent's reply landing is the moment
      // its halo should go out) — and once more after the onset window, for the
      // turn a human's message just STARTED. One trailing ask per burst, not a timer.
      again()
      if (onset) clearTimeout(onset)
      onset = setTimeout(() => {
        onset = null
        again()
      }, TURN_ONSET_MS)
    })
    // The BFF's push: an agent's busy bit flipped on its machine's link and this
    // conversation holds it — the one signal that reaches a FRIEND with no other
    // event (a terminal-driven turn). This is what makes the halo timely; the
    // message triggers and the lease below remain the nets under it.
    const offPoke = onChatBusy((cid) => {
      if (cid === convId) again()
    })
    const onVisible = () => {
      if (document.visibilityState === 'visible') again()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      offOpen()
      offFrame()
      offPoke()
      document.removeEventListener('visibilitychange', onVisible)
      if (onset) clearTimeout(onset)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId])

  // The lease: re-arm one re-ask per fresh answer while it claims someone active.
  useEffect(() => {
    if (!convId) return
    if (!(q.data?.agents ?? []).some((a) => a.status === 'active')) return
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
    const lease = setTimeout(() => void q.refetch(), ACTIVE_LEASE_MS)
    return () => clearTimeout(lease)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId, q.data])

  return useMemo(() => {
    const m = new Map<string, ConvBusyAgent>()
    for (const a of q.data?.agents ?? []) if (a.hash) m.set(a.hash, a)
    return m
  }, [q.data])
}
